import React, { useEffect, useMemo } from 'react';
import { ExpandMore } from '@mui/icons-material';
import { useTable, useExpanded, useFlexLayout } from 'react-table';
import useStyles from './styles';

export interface HeadCellsWithToggle {
  Header:
    | string
    | (({
        getToggleAllRowsExpandedProps,
        isAllRowsExpanded,
      }: {
        getToggleAllRowsExpandedProps: Function;
        isAllRowsExpanded: Function;
      }) => JSX.Element);
  Cell?: ({ row }: any) => JSX.Element | undefined;
  accessor: string;
  id?: undefined | string;
  width?: number;
  minWidth?: number;
}

const TreeDataView = ({
  headCells,
  data,
  maxHeight,
  updateHeight,
}: {
  headCells: HeadCellsWithToggle[];
  data: any;
  maxHeight: number;
  updateHeight?: Function;
}) => {
  const columns: any = React.useMemo(
    () => [
      {
        id: 'expander',
        width: 30,
        Cell: ({ row }: any) =>
          row.canExpand ? (
            <span {...row.getToggleRowExpandedProps({})}>
              <ExpandMore
                style={{
                  ...(row.isExpanded
                    ? { transform: 'rotate(0deg)' }
                    : { transform: 'rotate(-90deg)' }),
                  padding: '0px',
                  height: '15px',
                }}
              />
            </span>
          ) : null,
      },
      ...headCells,
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,
    state,
  } = useTable(
    {
      columns,
      data,
      autoResetExpanded: false,
    },
    useExpanded,
    useFlexLayout,
  );

  useMemo(() => toggleAllRowsExpanded(true), []);
  useEffect(() => {
    if (updateHeight) updateHeight();
  }, [state.expanded]);

  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div {...getTableProps()} className="table">
        <div className="thead">
          {headerGroups.map((headerGroup) => (
            <div className="tr" {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <div className="th" {...column.getHeaderProps()}>
                  {column.render('Header')}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="tbody" {...getTableBodyProps()}>
          <div style={{ maxHeight: `${maxHeight}px`, overflow: 'auto' }}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <div className="tr" {...row.getRowProps()}>
                  {row.cells.map((cell, index) => {
                    const properties = cell.getCellProps();
                    if (index === 0 && properties?.style) {
                      properties.style.marginLeft = `${row.depth * 10}px`;
                    }
                    if (index === 1 && properties?.style) {
                      properties.style.marginRight = '25px';
                    }
                    if (index === 2 && properties?.style) {
                      properties.style.marginLeft = `-${row.depth * 10}px`;
                    }
                    if (typeof (cell.value) !== 'object') {
                      return (
                        <div className="td" {...properties}>
                          {cell.render('Cell')}
                        </div>
                      );
                    }
                    return (
                      <div className="td" {...properties}>
                        N/A
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreeDataView;
